"use client";
import { STATS_API_URL } from "@/config/api";

import Image from "next/image";
import styles from "./info-tabs.module.scss";
import SlopeCharts from "@/components/knowledge-hub/SlopeCharts";

const SlopeAnalysisTab = () => {
  return (
    <div className={styles.tabContent}>
      <h2>Slope Analysis</h2>

      <p>
        The <strong>Slope Map Layers</strong> in AI2Peat provide insights into
        the topographic variation across Ireland’s peatlands. These layers
        (e.g., <em>Slope Map (based on NLCM)</em> for Raised Bogs, <em>Cutover Bog Slope Map (based on NLCM)</em> for Cutover,
        and <em>Blanket Bog Slope Map (based on NLCM)</em> for Blanket Bogs) help assess restoration
        potential, geomorphological conditions, and hydrological dynamics.
      </p>
      
      <h3>Why Slope Matters</h3>
      <p>
        Slope plays a key role in peatland formation and degradation. Steeper
        slopes can lead to surface runoff and erosion, while flatter terrain
        retains water and supports peat accumulation.
      </p>
        <li><strong>Hydrology:</strong> Flatter slopes retain water and promote peat accumulation; steeper slopes increase runoff risk.</li>
        <li><strong>Restoration:</strong> Low-gradient zones are more favorable for re-wetting; steep areas may need engineering solutions.</li>
        <li><strong>Landscape Assessment:</strong> Helps differentiate between intact bogs and cutover/degraded zones.</li>


      <h3>Example: Slope Analysis of a Selected Region</h3>

      <p>
        In the example below, a region within the <strong>Blanket Bog zone</strong> in western Ireland was selected 
        (coordinates approximately: 54.30°N to 53.91°N and –10.03°W to –8.61°W).The <strong>Blanket Bog</strong> layer 
        was enabled to examine terrain characteristics specific to blanket bogs in the area.
      </p>

      <video
        className={styles.image}
        autoPlay     
        muted    
        loop         
        playsInline     
        preload="metadata"
        poster="/info-page/slopeanalysis.png"
      >
        <source src="/info-page/slopemap.mp4" type="video/mp4" />

      </video>
      <p>
        A slope histogram was generated from the raster data, summarizing the
        area (in km²) for each slope interval class.
      </p>

      <SlopeCharts
        endpoint={STATS_API_URL}
        payload={{
          layer_name: "Slope_BB", 
          plot_type: "slope",
          polygon_coordinates: [
            { lat: 54.313319, lng: -9.975586 },
            { lat: 54.313319, lng: -8.349609 },
            { lat: 53.281637, lng: -8.349609 },
            { lat: 53.281637, lng: -9.975586 },
            { lat: 54.313319, lng: -9.975586 },
          ],
        }}
        className={styles.image}
        height={360}
        fallbackImage="/info-page/slope.png"
      />

    <p>
      The slope analysis shows that the majority of terrain falls within moderate slope classes such as 
      <strong>1.00–3.30°</strong> (Class D) and <strong>0.50–1.00°</strong> (Class C). However, notable portions 
      of the region exhibit steeper gradients above <strong>5.50°</strong> (Classes F and G), 
      particularly around the edges. This variety in slope indicates a mix of relatively flat peat accumulation zones 
      and more rugged, erosion-prone areas — a common pattern in blanket bog landscapes.
    </p>

      <h3>Why Slope Matters</h3>
      <div className={styles.tabUnorderedList}>
        <ul>
          <li>
            <strong>Hydrology:</strong> Flatter slopes retain water and promote
            peat accumulation; steeper slopes increase runoff risk.
          </li>
          <li>
            <strong>Restoration:</strong> Low-gradient zones are more favorable
            for re-wetting; steep areas may need engineering solutions.
          </li>
          <li>
            <strong>Landscape Assessment:</strong> Helps differentiate between
            intact bogs and cutover/degraded zones.
          </li>
        </ul>
      </div>
      <br />

      
    </div>
  );
};

export default SlopeAnalysisTab;
